import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { ArticleCalculationBase } from "helpers/pressCalculationBase";
import { downloadApiExport, downloadFile, getDateForFileName } from "helpers/utils";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { SubsetOf } from "types";

import type { AgreementSignatureStatus, AgreementStatus } from "./agreementsStore";
import type { PaymentStatus, TBeneficiaryPrecount, TFetchStatus, TParamsPagination } from "./types";
import type { TPortalType } from "./types";

export interface IArticle {
    id: number;
    titleId: number;
    titleName: string;
    beneficiaryId: number;
    beneficiary: string;
    name: string;
    price?: number;
    reference: string;
    packagePrice: number;
    packageQuantity: number;
    /**
     * Press rights base — folio count x price, or lump-sum amount. Never inferred from channel.type.
     */
    calculationBase?: ArticleCalculationBase | null;
    paidStatus: PaymentStatus;
    paymentDate: string | Date;
    grossDuty: number;
    netToPay: number;
    vat: number;
    withheldVat: number;
    contributions: number;
    workNature: string | null;
    contractSigned: AgreementSignatureStatus;
    contractId: string;
    contractSignedDate: string | Date;
    createdAt?: string | Date;
    lastSentAt?: string | Date;
    companyId: number;
    validBankInfos: 0 | 1;
    accountStatus: "pending" | "noaccount" | "confirmed";
    email: string;
    precount: number | string;
    channelId: number;
    ircecProfileId: number | null;
    invoiceNumbering?: number;
    /**
     * Full invoice number shown on statement
     */
    fullInvoiceNumber: string | undefined;
    completionStatus: ArticleCompletionStatus;
    completionDate?: string | Date | null;
    /**
     * Extra keys used to display article data for Agreements
     * The keys are consistent with those in the term data
     */
    beneficiarId?: number;
    author?: string;
    title?: string;
    agreementStatus: AgreementStatus | null;
}

/**
 * Article completion status
 */
export enum ArticleCompletionStatus {
    COMPLETED = "completed",
    PENDING = "pending",
}

export const fetchArticle = createAsyncThunk(
    "articles/fetchArticle",
    async ({ titleId, articleId }: { titleId: number; articleId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/${articleId}`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response);
        }
    },
);

export const fetchAllArticlesPaginated = createAsyncThunk(
    "titles/fetchAllArticlesPaginated",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/title/articles", {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response);
        }
    },
);

export const downloadDAPFilePress = createAsyncThunk(
    "articles/downloadDAPFile",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/title/articles", {
                    params: options,
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );
            const fileName = `export_dap_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getAllArticles = createAsyncThunk(
    "articles/getAllArticles",
    async (titleId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getAllArticlesBeneficiars = createAsyncThunk(
    "articles/getAllArticlesBeneficiars",
    async (beneficiaryId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiaryId}/article`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getAllArticlesBeneficiaryPortal = createAsyncThunk(
    "articles/getAllArticlesBeneficiaryPortal",
    async (titleId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/titles/${titleId}/articles`,
            );
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface ArticleSignedAgreement {
    id: number;
    filename: string | null;
    storageId: string | null;
    type: "agreement" | "avenant";
    lastUpdate: string | Date | null;
    companyId: number;
}

export const fetchArticleAgreements = createAsyncThunk(
    "articles/fetchArticleAgreements",
    async (articleId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get<{ data: ArticleSignedAgreement[] }>(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/articles/${articleId}/agreements`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchTitleSignedAgreements = createAsyncThunk(
    "articles/fetchTitleSignedAgreements",
    async (titleId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get<{ data: SignedDocument[] }>(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/titles/${titleId}/signed-documents`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface SignedDocument {
    id: number;
    type: "agreement" | "avenant";
    relatedResource: "article" | "frameworkContract" | "term";
    filename: string | null;
    storageId: string | null;
    signedDate: string | Date;
    companyId: number;
    articleId: number | null;
    articleName: string | null;
    termId: number | null;
    titleId: number | null;
    titleName: string | null;
    templateName: string | null;
}

export const fetchAllSignedDocuments = createAsyncThunk(
    "articles/fetchAllSignedDocuments",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get<{ data: SignedDocument[] }>(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/signed-documents`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchSignedDocumentContent = createAsyncThunk(
    "articles/fetchSignedDocumentContent",
    async (documentId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/signed-documents/${documentId}/download`,
            );
            return response.data as string;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const downloadSignedDocument = createAsyncThunk(
    "articles/downloadSignedDocument",
    async (document: SignedDocument, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(
                    import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT +
                        `/signed-documents/${document.id}/download`,
                ),
                {
                    pending: `${t("ShowStatements.PendingDownload")}`,
                    success: `${t("ShowStatements.SuccessDownload")}`,
                    error: `${t("ShowStatements.ErrorDownload")}`,
                },
            );
            const linkSource = `data:application/pdf;base64,${response.data}`;
            const fileName = document.filename?.split(".")[0] ?? `Document_${document.id}`;
            downloadFile({ source: linkSource, fileName, isLink: true });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const downloadArticleAgreement = createAsyncThunk(
    "articles/downloadArticleAgreement",
    async ({ articleId, agreement }: { articleId: number; agreement: ArticleSignedAgreement }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(
                    import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT +
                        `/articles/${articleId}/agreements/${agreement.id}`,
                ),
                {
                    pending: `${t("ShowStatements.PendingDownload")}`,
                    success: `${t("ShowStatements.SuccessDownload")}`,
                    error: `${t("ShowStatements.ErrorDownload")}`,
                },
            );
            const linkSource = `data:application/pdf;base64,${response.data}`;
            const fileName = agreement.filename?.split(".")[0] ?? `Contrat_${agreement.id}`;
            downloadFile({ source: linkSource, fileName, isLink: true });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchCreateNewArticle = createAsyncThunk(
    "articles/fetchCreateNewArticle",
    async (
        { data, portalType }: { data: any; portalType: SubsetOf<TPortalType, "audiovisual" | "press"> },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${data.titleId}/article`,
                {
                    ...data,
                },
            );
            if (response.status !== 200) throw Error;
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchUpdateArticleFromPublication = createAsyncThunk(
    "articles/fetchUpdateArticleFromPublication",
    async (
        {
            data,
            titleId,
            articleId,
            from,
        }: { data: any; titleId: number; articleId: number; from: "management" | "publication" },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/${articleId}`,
                { ...data },
            );
            if (response.status !== 200) throw Error;
            return { data: response.data.data, articleId, from };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchUpdateArticleFromBeneficiary = createAsyncThunk(
    "articles/fetchUpdateArticleFromBeneficiary",
    async (
        { data, beneficiaryId, articleId }: { data: any; beneficiaryId: number; articleId: number },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiaryId}/article/${articleId}`,
                { ...data },
            );
            if (response.status !== 200) throw Error;
            return { data: response.data.data, articleId };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchDeleteArticle = createAsyncThunk(
    "articles/fetchDeleteArticle",
    async ({ titleId, articleId }: { titleId: number; articleId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/${articleId}`,
            );
            if (response.status !== 200) throw Error;
            return articleId;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const exportArticles = createAsyncThunk(
    "articles/exportArticles",
    async (titleId: number, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/export`, {
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Articles.ExportLoading")}`,
                    success: `${t("Articles.ExportSucceeded")}`,
                    error: `${t("Articles.ExportFailed")}`,
                },
            );
            const fileName = `export_articles_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const exportAllArticles = createAsyncThunk(
    "articles/exportAllArticles",
    async ({ startDate, endDate }: { startDate: Date; endDate: Date }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/article/export`, {
                    params: { since: startDate, until: endDate },
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Articles.ExportLoading")}`,
                    success: `${t("Articles.ExportSucceeded")}`,
                    error: `${t("Articles.ExportFailed")}`,
                },
            );
            const fileName = `export_articles_${getDateForFileName(startDate)}_to_${getDateForFileName(endDate)}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const exportBeneficiaryArticles = createAsyncThunk(
    "articles/exportBeneficiaryArticles",
    async (beneficiaryId: number, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiaryId}/article/export`, {
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );
            const fileName = `export_articles_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const exportParutionAccounting = createAsyncThunk(
    "articles/exportParutionAccounting",
    async ({ titleId, ...data }: { titleId: number }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/accounting`, {
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                    params: data,
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            downloadFile({
                source: response.data,
                fileName: `export_comptable_${titleId}_${getDateForFileName()}.zip`,
            });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export type ArticlePaidPayload = {
    titleId: number;
    articleId: number;
    paymentData: {
        paidStatus: 0 | 1 | 2;
        paymentDate: Date | null;
        from: "management" | "publication" | "beneficiary";
    };
};
export const putArticlePaid = createAsyncThunk(
    "articles/putArticlePaid",
    async ({ titleId, articleId, paymentData }: ArticlePaidPayload, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/${articleId}/payment`,
                paymentData,
            );
            return {
                articleId,
                value: paymentData.paidStatus,
                from: paymentData.from,
                paymentDate: paymentData.paymentDate,
            };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putArticlePaidBeneficiary = createAsyncThunk(
    "articles/putArticlePaidBeneficiary",
    async (
        {
            beneficiarId,
            articleId,
            paymentData,
        }: {
            beneficiarId: number;
            articleId: number;
            paymentData: { paidStatus: 0 | 1 | 2; paymentDate: Date | null };
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiarId}/article/${articleId}/payment`,
                paymentData,
            );
            // return response.data;
            return { articleId, value: paymentData.paidStatus, paymentDate: paymentData.paymentDate };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putArticleDate = createAsyncThunk(
    "articles/putArticleDate",
    async (
        { titleId, articleId, paymentDate }: { titleId: number; articleId: number; paymentDate: Date },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/${articleId}/payment`,
                { paymentDate },
            );
            // return response.data;
            return { articleId, value: paymentDate };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putArticlePaidBulk = createAsyncThunk(
    "articles/putArticlePaidBulk",
    async ({ id, status }: { id: number[]; status: 0 | 1 | 2 }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/title/article/bulk/payment`, {
                id,
                status,
            });
            // return response.data;
            return { id, status };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getOldestArticlePaid = createAsyncThunk(
    "articles/getOldestArticlePaid",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/article/date`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putUpdateArticleContract = createAsyncThunk(
    "articles/putUpdateArticleContract",
    async (
        {
            titleId,
            articleId,
            email,
            templateId,
            userVariables,
        }: {
            titleId: number;
            articleId: number;
            email: string;
            templateId: number;
            userVariables?: any;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/${articleId}/agreement`,
                { email, sendContract: true, templateId, userVariables },
            );
            if (response.status !== 200) throw Error;
            return articleId;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const sendManualContract = createAsyncThunk(
    "articles/sendManualContract",
    async (
        {
            titleId,
            articleId,
            file,
            signatureDate,
            from,
        }: {
            titleId: number;
            articleId: number;
            file: FormData;
            signatureDate: Date;
            from: "management" | "publication" | "beneficiary";
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/${articleId}/agreement`,
                file,
                { params: { signatureDate } },
            );
            // return {articleId, signatureDate};
            return { data: response.data.data, from };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

type ArticleAgreementDeleteFrom = "management" | "publication" | "beneficiary";
type ArticleAgreementDeleteType = "manual" | "sent";

// Delete a manual contract upload or cancel a contract sent for signature
export const deleteManualContract = createAsyncThunk(
    "articles/deleteManualContract",
    async (
        {
            titleId,
            articleId,
            from,
            deleteType = "manual",
        }: {
            titleId: number;
            articleId: number;
            from: ArticleAgreementDeleteFrom;
            deleteType?: ArticleAgreementDeleteType;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/${articleId}/agreement`,
            );
            return { data: response.data, from, deleteType };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putArticleCompletionStatus = createAsyncThunk(
    "articles/putUpdateArticleCompletionStatus",
    async (
        {
            completionStatus,
            completionDate,
            titleId,
            articleId,
            from,
        }: {
            completionStatus: ArticleCompletionStatus;
            completionDate?: Date | null;
            titleId: number;
            articleId: number;
            from: "management" | "publication-or-beneficiary";
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/article/${articleId}/completion-status`,
                { completionStatus, completionDate },
            );
            const data = response.data.data;
            return { data, from, articleId };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface ArticleState {
    allArticles: ArticlePaginated | null; // articles for management page
    articles: IArticle[]; // articles for a title
    /** Parution id the `articles` list was requested for. Stale responses for another id are ignored. */
    articlesTitleId: number | null;
    articleStatus: TFetchStatus;
    articleExportStatus: TFetchStatus;
    sendSignatureStatus: TFetchStatus;
    contractSigned: AgreementSignatureStatus | undefined;
    fetchAllArticlesStatus: TFetchStatus;
    article: IArticle;
    oldestArticlePaidDate: string | null;
    articleAgreements: ArticleSignedAgreement[];
    articleAgreementsStatus: TFetchStatus;
    titleSignedAgreements: SignedDocument[];
    titleSignedAgreementsStatus: TFetchStatus;
    allSignedDocuments: SignedDocument[];
    allSignedDocumentsStatus: TFetchStatus;
    viewedSignedDocument: string | null;
    viewedSignedDocumentStatus: TFetchStatus;
}

const initialState: ArticleState = {
    allArticles: null, // articles for management page
    articles: [], // articles for a title
    articlesTitleId: null,
    articleStatus: "idle",
    articleExportStatus: "idle",
    sendSignatureStatus: "idle",
    contractSigned: undefined,
    fetchAllArticlesStatus: "idle",
    article: {} as IArticle,
    oldestArticlePaidDate: "",
    articleAgreements: [],
    articleAgreementsStatus: "idle",
    titleSignedAgreements: [],
    titleSignedAgreementsStatus: "idle",
    allSignedDocuments: [],
    allSignedDocumentsStatus: "idle",
    viewedSignedDocument: null,
    viewedSignedDocumentStatus: "idle",
};

export type ManagementArticle = IArticle & {
    beneficiaryId: number;
    beneficiaryAvatarUrl: string | null;
    titleName: string;
    titleEan13: string;
    titleAvatarUrl: string | null;
    email: string;
    precount: TBeneficiaryPrecount;
    beneficiary: string;
    agreementStatus: AgreementStatus | null;
};
export interface ArticlePaginated {
    count: number;
    offset: number;
    entries: ManagementArticle[];
}

export const articleSlice = createSlice({
    name: "articles",
    initialState,
    reducers: {
        addArticles: (state, action) => {
            state.articles = action.payload.concat(state.articles);
        },
        setLastSentAtTerms(state, action) {
            state.articles = state.articles.map((art) => {
                if (action.payload.includes(art.id)) return { ...art, lastSentAt: new Date() };
                return art;
            });
        },
        setContractSigned(state, action) {
            state.contractSigned = action.payload;
        },
        resetArticle(state) {
            state.article = {} as IArticle;
            state.articleStatus = "idle";
        },
        updateArticles(state, action) {
            const newArticlesMap = new Map(action.payload.map((article) => [article.id, article]));
            state.articles = state.articles.map((article) => {
                const updatedArticle = newArticlesMap.get(article.id);
                return updatedArticle ? { ...article, ...updatedArticle } : article;
            });
        },
        resetArticles(state) {
            state.articles = [];
            state.articlesTitleId = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchAllArticlesPaginated.pending, (state, action) => {
            state.fetchAllArticlesStatus = "pending";
        });
        builder.addCase(fetchAllArticlesPaginated.fulfilled, (state, action) => {
            state.allArticles = action.payload;
            state.fetchAllArticlesStatus = "succeeded";
        });
        builder.addCase(fetchAllArticlesPaginated.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.fetchAllArticlesStatus = "failed";
        });
        builder.addCase(getAllArticles.fulfilled, (state, action) => {
            if (state.articlesTitleId !== Number(action.meta.arg)) return;
            state.articles = action.payload.data.reverse();
            state.articleStatus = "succeeded";
        });
        builder.addCase(getAllArticles.rejected, (state, action) => {
            if (state.articlesTitleId !== Number(action.meta.arg)) return;
            state.articleStatus = "failed";
            const translation = translationsWithFallback(
                (action.payload as any)?.message,
                "ServerError.InternalServerError",
            );
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(getAllArticles.pending, (state, action) => {
            const titleId = Number(action.meta.arg);
            state.articleStatus = "pending";
            if (state.articlesTitleId !== titleId) {
                state.articles = [];
                state.articlesTitleId = titleId;
            }
        });

        builder.addCase(getAllArticlesBeneficiars.fulfilled, (state, action) => {
            state.articles = action.payload.data.reverse();
            state.articleStatus = "succeeded";
        });
        builder.addCase(getAllArticlesBeneficiars.rejected, (state, { payload }) => {
            state.articleStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(getAllArticlesBeneficiars.pending, (state, action) => {
            state.articleStatus = "pending";
        });

        builder.addCase(getAllArticlesBeneficiaryPortal.fulfilled, (state, action) => {
            state.articles = action.payload.reverse();
            state.articleStatus = "succeeded";
        });
        builder.addCase(getAllArticlesBeneficiaryPortal.rejected, (state, { payload }) => {
            state.articleStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(getAllArticlesBeneficiaryPortal.pending, (state, action) => {
            state.articleStatus = "pending";
        });

        builder.addCase(fetchArticleAgreements.pending, (state) => {
            state.articleAgreementsStatus = "pending";
        });
        builder.addCase(fetchArticleAgreements.fulfilled, (state, action) => {
            state.articleAgreements = action.payload;
            state.articleAgreementsStatus = "succeeded";
        });
        builder.addCase(fetchArticleAgreements.rejected, (state) => {
            state.articleAgreements = [];
            state.articleAgreementsStatus = "failed";
        });

        builder.addCase(fetchTitleSignedAgreements.pending, (state) => {
            state.titleSignedAgreementsStatus = "pending";
        });
        builder.addCase(fetchTitleSignedAgreements.fulfilled, (state, action) => {
            state.titleSignedAgreements = action.payload;
            state.titleSignedAgreementsStatus = "succeeded";
        });
        builder.addCase(fetchTitleSignedAgreements.rejected, (state) => {
            state.titleSignedAgreements = [];
            state.titleSignedAgreementsStatus = "failed";
        });

        builder.addCase(fetchAllSignedDocuments.pending, (state) => {
            state.allSignedDocumentsStatus = "pending";
        });
        builder.addCase(fetchAllSignedDocuments.fulfilled, (state, action) => {
            state.allSignedDocuments = action.payload;
            state.allSignedDocumentsStatus = "succeeded";
        });
        builder.addCase(fetchAllSignedDocuments.rejected, (state) => {
            state.allSignedDocuments = [];
            state.allSignedDocumentsStatus = "failed";
        });

        builder.addCase(fetchSignedDocumentContent.pending, (state) => {
            state.viewedSignedDocumentStatus = "pending";
            state.viewedSignedDocument = null;
        });
        builder.addCase(fetchSignedDocumentContent.fulfilled, (state, action) => {
            state.viewedSignedDocument = action.payload;
            state.viewedSignedDocumentStatus = "succeeded";
        });
        builder.addCase(fetchSignedDocumentContent.rejected, (state) => {
            state.viewedSignedDocument = null;
            state.viewedSignedDocumentStatus = "failed";
        });

        builder.addCase(fetchCreateNewArticle.fulfilled, (_, action) => {
            const portalType = action.meta.arg.portalType;
            toast.success(`${t(`NewArticle.PostSucceeded_${portalType}`)}`);
        });
        builder.addCase(fetchCreateNewArticle.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchUpdateArticleFromPublication.fulfilled, (state, action) => {
            toast.success(`${t("Articles.EditSucceeded")}`);
            const { articleId, data, from } = action.payload;
            if (from == "publication") {
                state.articles = state.articles.map((art) => {
                    if (art.id == articleId) return { ...art, ...data };
                    return art;
                });
            }
            if (from == "management") {
                const updatedArticles = state.allArticles?.entries.map((art) => {
                    if (art.id == articleId) return { ...art, ...data };
                    return art;
                });
                state.allArticles = { ...state.allArticles, entries: updatedArticles } as ArticlePaginated;
            }
        });
        builder.addCase(fetchUpdateArticleFromPublication.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchUpdateArticleFromBeneficiary.fulfilled, (state, action) => {
            toast.success(`${t("Articles.EditSucceeded")}`);
            state.articles = state.articles.map((art) => {
                if (art.id == action.payload.articleId) return { ...art, ...action.payload.data };
                return art;
            });
        });
        builder.addCase(fetchUpdateArticleFromBeneficiary.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchDeleteArticle.fulfilled, (state, action) => {
            toast.success(`${t("Articles.DeleteSucceeded")}`);
            const deletedId = action.payload;
            state.articles = state.articles.filter((art) => art.id != deletedId);
            // Update management articles state
            const { count = 0, entries: articles } = { ...state.allArticles };
            const updatedArticlesState = { count: count - 1, entries: articles?.filter((art) => art.id !== deletedId) };
            state.allArticles = updatedArticlesState as ArticlePaginated;
        });
        builder.addCase(fetchDeleteArticle.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(exportArticles.fulfilled, (state, action) => {
            state.articleExportStatus = "succeeded";
        });
        builder.addCase(exportArticles.rejected, (state) => {
            state.articleExportStatus = "failed";
        });
        builder.addCase(exportArticles.pending, (state, action) => {
            state.articleExportStatus = "pending";
        });
        builder.addCase(exportBeneficiaryArticles.fulfilled, (state, action) => {
            state.articleExportStatus = "succeeded";
            toast.success(`${t("Articles.ExportSucceeded")}`);
        });
        builder.addCase(exportBeneficiaryArticles.rejected, (state) => {
            state.articleExportStatus = "failed";
        });
        builder.addCase(exportBeneficiaryArticles.pending, (state, action) => {
            state.articleExportStatus = "pending";
            // toast.info(`${t('Articles.ExportLoading')}`);
        });

        builder.addCase(putArticlePaid.fulfilled, (state, action) => {
            toast.success(`${t("Articles.PaidStatusSuccess")}`);
            const { articleId, value, from, paymentDate } = action.payload;

            // Store as UTC midnight of the local calendar day so state matches what
            // the backend would return on a fresh fetch (DATE column semantics).
            const updatedDate = paymentDate
                ? (() => {
                      const d = new Date(paymentDate as Date);
                      return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
                  })()
                : undefined;

            if (["publication", "beneficiary"].includes(from)) {
                state.articles = state.articles.map((art) => {
                    if (art.id == articleId) {
                        return {
                            ...art,
                            paidStatus: value,
                            ...(updatedDate !== undefined && { paymentDate: updatedDate }),
                        };
                    }
                    return art;
                });
            }
            if (["management"].includes(from)) {
                const articlesList = state.allArticles?.entries.map((art) => {
                    if (art.id == articleId) {
                        return {
                            ...art,
                            paidStatus: value,
                            ...(updatedDate !== undefined && { paymentDate: updatedDate }),
                        };
                    }
                    return art;
                });
                state.allArticles = { ...state.allArticles, entries: articlesList } as ArticlePaginated;
            }
        });
        builder.addCase(putArticlePaid.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(putArticleDate.fulfilled, (state, action) => {
            toast.success(`${t("Articles.PaidDateSuccess")}`);
            const currentArticles = JSON.parse(JSON.stringify(state.articles));
            currentArticles.find((item: IArticle) => item.id == action.payload.articleId).paymentDate =
                action.payload.value;
            state.articles = currentArticles;
        });
        builder.addCase(putArticleDate.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(putArticlePaidBeneficiary.fulfilled, (state, action) => {
            toast.success(`${t("Articles.PaidStatusSuccess")}`);
            const { articleId, value, paymentDate } = action.payload;
            // Store as UTC midnight of the local calendar day so state matches what
            // the backend would return on a fresh fetch (DATE column semantics).
            const date = paymentDate
                ? (() => {
                      const d = new Date(paymentDate as Date);
                      return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
                  })()
                : undefined;
            state.articles = state.articles.map((art) => {
                if (art.id == articleId)
                    return { ...art, paidStatus: value, ...(date !== undefined && { paymentDate: date }) };
                return art;
            });
        });
        builder.addCase(putArticlePaidBeneficiary.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(putArticlePaidBulk.fulfilled, (state, action) => {
            toast.success(`${t("Articles.PaidStatusSuccess")}`);
            const location = window.location.pathname;
            const { status: paidStatus, id: articleIds } = action.payload;
            // UTC midnight of today's local calendar day — matches DATE column semantics.
            const now = new Date();
            const paymentDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

            if (location === "/management") {
                const updatedArticles = state.allArticles?.entries.map((art) => {
                    if (articleIds.includes(art.id)) return { ...art, paidStatus, paymentDate };
                    return art;
                });
                state.allArticles = { ...state.allArticles, entries: updatedArticles } as ArticlePaginated;
                return;
            }
            state.articles = state.articles.map((art) => {
                if (articleIds.includes(art.id)) return { ...art, paidStatus, paymentDate };
                return art;
            });
        });
        builder.addCase(putArticlePaidBulk.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(putUpdateArticleContract.fulfilled, (state, action) => {
            toast.success(`${t("Articles.SignatureSuccess")}`);
            state.sendSignatureStatus = "succeeded";
            state.articles = state.articles.map((art) => {
                if (art.id == action.payload) return { ...art, contractSigned: 1, lastSentAt: new Date() };
                return art;
            });
        });
        builder.addCase(putUpdateArticleContract.pending, (state, action) => {
            state.sendSignatureStatus = "pending";
        });
        builder.addCase(putUpdateArticleContract.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.sendSignatureStatus = "failed";
        });

        builder.addCase(sendManualContract.fulfilled, (state, { payload }) => {
            toast.success(`${t("Articles.ManualSignatureSuccess")}`);
            state.sendSignatureStatus = "succeeded";
            const { data, from } = payload;

            if (["publication", "beneficiary"].includes(from)) {
                state.articles = state.articles.map((art) => {
                    if (art.id == data.id)
                        return {
                            ...art,
                            contractSigned: 2,
                            contractSignedDate: data.contractSignedDate,
                            contractId: data.contractId,
                        };
                    return art;
                });
            }
            if (["management"].includes(from)) {
                const updatedArticles = state.allArticles?.entries.map((art) => {
                    if (art.id == data.id) {
                        return {
                            ...art,
                            contractSigned: 2,
                            contractSignedDate: data.contractSignedDate,
                            contractId: data.contractId,
                        };
                    }

                    return art;
                });
                state.allArticles = { ...state.allArticles, entries: updatedArticles } as ArticlePaginated;
            }
        });
        builder.addCase(sendManualContract.pending, (state, action) => {
            state.sendSignatureStatus = "pending";
        });
        builder.addCase(sendManualContract.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.sendSignatureStatus = "failed";
        });
        builder.addCase(deleteManualContract.fulfilled, (state, action) => {
            const { from, deleteType } = action.payload;
            const { articleId: deletedId } = action.meta.arg;
            const successKey =
                deleteType === "sent" ? "Articles.CancelContractSendSuccess" : "Terms.PDFDeletedSuccessfully";

            if (["publication", "beneficiary"].includes(from)) {
                state.articles = state.articles.map((art) => {
                    if (art.id == deletedId) return { ...art, contractSigned: 0, contractId: "" };
                    return art;
                });
            }
            if (["management"].includes(from)) {
                const updatedArticles = state.allArticles?.entries.map((art) => {
                    if (art.id == deletedId) return { ...art, contractSigned: 0, contractId: "" };
                    return art;
                });
                state.allArticles = { ...state.allArticles, entries: updatedArticles } as ArticlePaginated;
            }
            toast.success(`${t(successKey)}`);
        });
        builder.addCase(deleteManualContract.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchArticle.pending, (state, action) => {
            state.articleStatus = "pending";
        });
        builder.addCase(fetchArticle.fulfilled, (state, action) => {
            state.article = action.payload.data;
            state.articleStatus = "succeeded";
        });
        builder.addCase(fetchArticle.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.articleStatus = "failed";
        });
        builder.addCase(putArticleCompletionStatus.fulfilled, (state, action) => {
            const { completionStatus, completionDate } = action.payload.data;
            const { from, articleId } = action.payload;

            if ("publication-or-beneficiary" === from) {
                state.articles = state.articles.map((art) => {
                    if (art.id == articleId) return { ...art, completionStatus, completionDate };
                    return art;
                });
            }
            if ("management" === from) {
                const updatedArticles = state.allArticles?.entries.map((art) => {
                    if (art.id == articleId) {
                        return { ...art, completionStatus, completionDate };
                    }
                    return art;
                });
                state.allArticles = { ...state.allArticles, entries: updatedArticles } as ArticlePaginated;
            }
            toast.success(`${t("Articles.CompletionStatusSuccess")}`);
        });
        builder.addCase(putArticleCompletionStatus.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(getOldestArticlePaid.fulfilled, (state, { payload }) => {
            state.oldestArticlePaidDate = payload;
        });
    },
});
export const { addArticles, setLastSentAtTerms, setContractSigned, resetArticle, updateArticles, resetArticles } =
    articleSlice.actions;
export default articleSlice.reducer;
